/**
 * PATH: packages/ui/src/kanban/api-fetch/cardWireBoundary.ts
 * FILE_ID: UI-KANBAN-FETCH-CARDWIRE-001
 * PURPOSE: Convert untrusted card HTTP payloads into renderer-safe Card values.
 * WHY: Endpoint generation may lag serializer fields; no renderer may consume raw wire data.
 * FLOW: unknown HTTP body -> card-surface normalizer -> safe Card or boundary error.
 * DEPENDENCIES: card-surface/model/normalizeCardWire; types/card.
 * RELATED: cards.ts; board-view/usePrioritySnapshotSeed.ts.
 */
import {
  normalizeCardWire,
  type CardWireIssue,
} from '../card-surface/model/normalizeCardWire'
import type { Card, CardLanePlacement } from '../types'

export class CardWireBoundaryError extends Error {
  readonly endpoint: string
  readonly issues: readonly CardWireIssue[]

  constructor(endpoint: string, issues: readonly CardWireIssue[]) {
    super(
      `Card wire contract failed for ${endpoint}: ${issues
        .map((issue) => `${issue.field} ${issue.message}`)
        .join('; ')}`,
    )
    this.name = 'CardWireBoundaryError'
    this.endpoint = endpoint
    this.issues = issues
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasOwn(value: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key)
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function asNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function asNumberArray(value: unknown): number[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is number => typeof item === 'number' && Number.isFinite(item),
      )
    : []
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function boundaryIssue(
  field: string,
  message: string,
): CardWireIssue {
  return { field, message }
}

/**
 * The Card surface model intentionally uses neutral names. Rehydrate the
 * established UI Card shape here, preserving unknown server-owned display
 * fields while stripping the client-only placement instance identity.
 */
export function normalizeCardWireForRenderer(
  value: unknown,
  endpoint: string,
): Card {
  if (!isRecord(value)) {
    throw new CardWireBoundaryError(endpoint, [
      boundaryIssue('$', 'Card payload must be an object.'),
    ])
  }
  if (hasOwn(value, 'access_locked') && typeof value.access_locked !== 'boolean') {
    throw new CardWireBoundaryError(endpoint, [
      boundaryIssue('access_locked', 'Access lock state must be boolean when present.'),
    ])
  }

  const result = normalizeCardWire(value)
  if (!result.ok) {
    throw new CardWireBoundaryError(endpoint, result.issues)
  }
  const { model } = result
  const unsafePlacementIssues = result.compatibilityIssues.filter(
    (issue) =>
      issue.message !== 'Accepted legacy JSON-string placement payload.',
  )
  if (unsafePlacementIssues.length > 0) {
    throw new CardWireBoundaryError(endpoint, unsafePlacementIssues)
  }
  const issues: CardWireIssue[] = []
  if (model.organisationId === null) {
    issues.push(boundaryIssue('organisation', 'Card organisation must be a number.'))
  }
  if (model.laneId === null) {
    issues.push(boundaryIssue('lane', 'Card lane must be a non-empty string.'))
  }
  if (typeof value.created_at !== 'string' || typeof value.updated_at !== 'string') {
    issues.push(
      boundaryIssue(
        'timestamps',
        'Card created_at and updated_at must be strings.',
      ),
    )
  }
  if (issues.length > 0) {
    throw new CardWireBoundaryError(endpoint, issues)
  }

  const { placement_id: _clientOnlyPlacementId, ...wire } = value
  const placements: CardLanePlacement[] = model.placements.map((placement) => ({
    id: placement.id,
    card: placement.cardId,
    lane: placement.laneId,
    position: placement.position,
    lane_name: placement.laneName,
    board: placement.boardId,
    board_name: placement.boardName,
    board_state: placement.boardState,
    board_accessible: placement.boardAccessible,
  }))
  const redacted = model.access.locked

  return {
    ...wire,
    id: model.id,
    organisation: model.organisationId!,
    lane: model.laneId!,
    title: redacted ? 'Locked item' : model.title,
    summary: redacted ? '' : model.summary,
    primary_email: redacted ? null : model.primaryEmailId,
    related_emails: redacted ? [] : asStringArray(wire.related_emails),
    related_attachment_ids_json: redacted
      ? []
      : asStringArray(wire.related_attachment_ids_json),
    job_id: asNullableNumber(wire.job_id),
    bill: asNullableString(wire.bill),
    variation_id: asNullableString(wire.variation_id),
    invoice_id: asNullableString(wire.invoice_id),
    quote_id: asNullableString(wire.quote_id),
    confidence: asNullableNumber(wire.confidence),
    suggested_lane: asNullableString(wire.suggested_lane),
    locked_at: asNullableString(wire.locked_at),
    locked_by: asNullableNumber(wire.locked_by),
    swimlane_user: asNullableNumber(wire.swimlane_user),
    status: model.status,
    snoozed_until: asNullableString(wire.snoozed_until),
    blocked_reason: asString(wire.blocked_reason),
    due_date: model.dueDate,
    start_date: model.startDate,
    effort_days: asNullableNumber(wire.effort_days),
    priority: model.priority,
    audit_log_json: redacted ? [] : asArray<Card['audit_log_json'][number]>(wire.audit_log_json),
    sensitive: redacted || wire.sensitive === true,
    sensitivity_reason: redacted ? '' : asString(wire.sensitivity_reason),
    tags: redacted ? [] : asArray<Card['tags'][number]>(wire.tags),
    assigned_user_ids: redacted ? [] : asNumberArray(wire.assigned_user_ids),
    comments_count: redacted ? 0 : model.commentsCount,
    checklist_summary: redacted
      ? { total: 0, done: 0 }
      : { ...model.checklist },
    lane_placements: redacted ? [] : placements,
    created_at: value.created_at,
    updated_at: value.updated_at,
    created_by: asNullableNumber(wire.created_by),
    access_locked: model.access.locked,
    access_lock_state: model.access.state ?? undefined,
    access_lock_id: model.access.lockId ?? undefined,
  } as Card
}

export function normalizeCardListWireForRenderer(
  value: unknown,
  endpoint: string,
): Card[] {
  if (!Array.isArray(value)) {
    throw new CardWireBoundaryError(endpoint, [
      boundaryIssue('$', 'Card list payload must be an array.'),
    ])
  }
  return value.map((card, index) =>
    normalizeCardWireForRenderer(card, `${endpoint}[${index}]`),
  )
}
